import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import { QuarterlyEntry } from '../../hooks/useKPIs'

interface Props {
  data: QuarterlyEntry[]
}

function formatEurK(v: number) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M €`
  if (v >= 1_000) return `${Math.round(v / 1_000)}k €`
  return `${v} €`
}

function formatQuarter(q: string) {
  // "2026-Q2" → "Q2 '26"
  const parts = q.split('-')
  if (parts.length === 2) return `${parts[1]} '${parts[0].slice(2)}`
  return q
}

const CustomTooltip = ({ active, payload, label }: {
  active?: boolean
  payload?: { value: number }[]
  label?: string
}) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-[--km-border] bg-[#141b28] px-3 py-2 text-xs shadow-lg">
      <p className="font-medium text-[--km-text-secondary] mb-1">{label}</p>
      <p className="text-[--km-text-primary]">
        {new Intl.NumberFormat('it-IT', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        }).format(payload[0].value)}
      </p>
    </div>
  )
}

export function RecurringRevenueChart({ data }: Props) {
  if (!data.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm font-medium text-[--km-text-secondary]">
          Nessun dato forecast trimestrale
        </p>
        <p className="text-xs text-[--km-text-muted]">
          Carica contratti ricorrenti per attivare il forecast
        </p>
      </div>
    )
  }

  const chartData = data.map(d => ({
    label: formatQuarter(d.quarter),
    amount: Number(d.amount),
  }))

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: 4, right: 16, left: 4, bottom: 0 }} barSize={32}>
        <defs>
          <linearGradient id="recurringBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.85} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.45} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.06)" />
        <XAxis
          dataKey="label"
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tickFormatter={formatEurK}
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={52}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99,102,241,0.08)' }} />
        <Bar dataKey="amount" fill="url(#recurringBarGrad)" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}
